import React, { useEffect } from 'react';

const faqs = [
  {
    question: 'What is E-Moto?',
    answer:
      'E-Moto is a people-powered automotive platform that connects drivers with vetted local agents to buy, sell, inspect, and move vehicles or parts. Every agent has community reviews, transparent pricing, and protected payment options so you can make confident decisions.'
  },
  {
    question: 'How does E-Moto primarily work?',
    answer:
      'E-Moto connects everyday drivers with on-the-ground automotive pros who use their experience to help you buy, inspect, or sell. Agents build their own reputation and personal brand through reviews, so they’re motivated to deliver for you—not just a dealership or company—whether you need advice, a trusted set of eyes, or hands-on help.'
  },
  {
    question: 'Why did we create E-Moto?',
    answer:
      'E-Moto is built for the people of Mzansi. We want automotive experts to earn from their skills, go independent, or make extra income in a tough job market. At the same time, everyday drivers deserve affordable access to trusted help, so we connect you with the right expertise without the overhead or surprises.'
  },
  {
    question: 'Is E-Moto available across South Africa?',
    answer:
      'Yes. We operate across major metros including Johannesburg, Pretoria, Cape Town, Durban, and surrounding towns. Tell us where you are and we will match you with nearby agents who know local roads, licensing offices, and inspection partners.'
  },
  {
    question: 'What is included in a pre-purchase car inspection?',
    answer:
      'A local agent checks the vehicle in person, verifies VIN and engine numbers, documents condition with photos, and flags red flags or negotiation points. You receive a clear report inside E-Moto so you can proceed or pause with confidence.'
  },
  {
    question: 'How much does a pre-purchase inspection cost in South Africa?',
    answer:
      'Costs depend on the vehicle type and location. Agents publish their pricing upfront, and you see the quote before you approve the booking so there are no surprises.'
  },
  {
    question: 'How do I check a vehicle history in South Africa?',
    answer:
      'Agents can run vehicle history checks and compare records with the physical inspection. You get evidence in one place, helping you avoid accident history or paperwork issues.'
  },
  {
    question: 'Can E-Moto help me sell my car fast or privately?',
    answer:
      'Yes. Agents help with listings, photos, pricing, and buyer screening so you can sell your car fast without getting flooded by time-wasters.'
  },
  {
    question: 'Do you provide used car valuation or price guidance?',
    answer:
      'Agents compare live listings and recent sales to estimate a fair range, giving you a practical used car price guide for negotiation or selling.'
  },
  {
    question: 'Do you help with roadworthy certificates and paperwork?',
    answer:
      'Yes. Agents guide you through roadworthy certificate requirements, transfer paperwork, and the documents needed to buy or sell a car in South Africa.'
  },
  {
    question: 'How do payments stay secure?',
    answer:
      'Payments are held in a protected flow until work is signed off. You approve milestones such as an inspection report or listing draft before funds are released.'
  },
  {
    question: 'Do I need to download an app?',
    answer:
      'You can open the E-Moto experience in your browser immediately. If you prefer mobile, add the app to your home screen for faster access; all chats and orders stay in sync.'
  },
  {
    question: 'How fast can I start?',
    answer:
      'Most people start a chat within minutes and receive inspection availability within the same day. For sellers, listings are typically prepared within 24 to 48 hours once photos are captured.'
  }
];

export const FaqPage = () => {
  useEffect(() => {
    const canonicalUrl = 'https://www.e-moto.io/faq';
    let canonicalTag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', canonicalUrl);

    let ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);
  }, []);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-brand-surface px-6 py-12 text-brand-text">
      <div className="mx-auto max-w-4xl space-y-8">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-dark">FAQ</p>
          <h1 className="font-display text-3xl font-semibold leading-tight">E-Moto Frequently Asked Questions</h1>
          <p className="text-brand-subtext">
            Answers about pre-purchase inspections, used car valuations, selling support, and how E-Moto connects you with trusted local agents.
          </p>
        </header>

        <section className="space-y-4">
          {faqs.map((item) => (
            <article key={item.question} className="rounded-2xl border border-[#DFEDEC] bg-white/90 p-6 shadow-sm shadow-brand/10">
              <h2 className="font-display text-lg font-semibold text-brand-text">{item.question}</h2>
              <p className="mt-2 text-sm leading-relaxed text-brand-subtext">{item.answer}</p>
            </article>
          ))}
        </section>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </div>
  );
};
